import { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '@/core/firebase/config';
import { getEnvDbPrefix } from '@/core/firebase/config';
import type { UserRole } from '@/features/auth/types';

interface UseGetUserRoleResult {
  role: UserRole | null;
  isLoading: boolean;
  error: Error | null;
}

export default function useGetUserRole(userId: string | null | undefined): UseGetUserRoleResult {
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const dbStringRef = getEnvDbPrefix(`user/${userId}/role`);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setRole(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    get(ref(db, dbStringRef))
      .then(d => {
        const data = d.val() as UserRole | null;
        // Handle backward compatibility: if role is not set, check isAdmin
        if (!data) {
          // Try to get isAdmin for backward compatibility
          const isAdminRef = getEnvDbPrefix(`user/${userId}/isAdmin`);
          return get(ref(db, isAdminRef)).then(adminData => {
            const isAdmin = adminData.val() as boolean | null;
            if (isAdmin === true) {
              return 'admin' as UserRole;
            }
            return 'user' as UserRole;
          });
        }
        return data;
      })
      .then(roleValue => {
        setRole(roleValue || 'user');
        setError(null);
      })
      .catch(err => {
        console.error('Error fetching user role:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch user role'));
        setRole(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId, dbStringRef]);

  return { role, isLoading, error };
}
